/**
 * Created by Ella Ma on 01/11/2021.
 * Description:
 * custom grid group row render
 * ------ maintenance history ------
 */

import { Component, OnDestroy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { RowNode } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { FieldType } from '../../../tamalelibs/models/workflow.model';
import { DateHelperWebService } from '../../../tamalelibs/services/date-helper.web.service';
import { NOT_START } from '../../../tamalelibs/services/workflow.service';
import { CustomGroupRowInnerComponentActions } from './custom-group-row-inner-render.model';

const BLANK_VALUE = '(Blanks)';

@Component({
    selector: 'tam-custom-group-row-inner-renderer',
    templateUrl: './custom-group-row-inner-renderer.component.html',
    styleUrls: ['./custom-group-row-inner-renderer.component.scss']
})

// tslint:disable-next-line: component-class-suffix
export class CustomGroupRowInnerRenderer implements ICellRendererAngularComp, OnDestroy {

    aggFuncColumns = [];
    groupValue;
    private _node: RowNode;
    private _services;
    private _destroySubscriptions: Array<Subscription> = [];

    agInit(params: any): void {
        this._node = params.node;
        this._services = params.services;
        if (params.isSpecifyGroupName) {
            if (params.node && params.node.allLeafChildren[0] && params.node.allLeafChildren[0].data.processInstance) {
                this.groupValue = params.node.allLeafChildren[0].data.processInstance.projectName;
            }
        } else {
            this.groupValue = this._formatValue();
        }
        this._initAggFun(params);
        this._destroySubscriptions.push(
            params.action$.subscribe(action => {
                if (action.type === CustomGroupRowInnerComponentActions.UPDATE_COLUMN_WIDTH_BY_OPERATION) {
                    this._resetStyle(params);
                } else if (action.type === CustomGroupRowInnerComponentActions.UPDATE_COLUMN_WIDTH_BY_SCROLL) {
                    this._updateGroupAggFuncValuePosition(action.payload);
                } else if (action.type === CustomGroupRowInnerComponentActions.UPDATE_COLUMN_AGGFUNC_VALUE) {
                    this._updateGroupAggFuncValue(params);
                }
            })
        );
    }

    isCellHidden(column: { width: number, left: number }) {
        return column.left < 0;
    }

    /**
    * ag grid IFilterComp optional
    */
    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    /**
     * Get the cell to refresh. Return true if successful. Return false if not (or you don't have refresh logic),
     * then the grid will refresh the cell for you
     */
    refresh(): boolean {
        return false;
    }

    private _formatValue() {
        if (this._node.key) {
            if (this._node.key === NOT_START) {
                return this._node.key;
            } else {
                const colDef = this._node.rowGroupColumn['colDef'];
                if (colDef.columnType === FieldType.DATE) {
                    // handle start date which is different from the adhoc date
                    if (colDef.field === 'start-date') {
                        return DateHelperWebService.getDateString(DateHelperWebService.parseDateFunc(this._node.key), 'MM/dd/yyyy HH:mm');
                    } else {
                        const value = parseInt(this._node.key, 10);
                        return DateHelperWebService.getDateString(new Date(value), 'MMM-dd-yyyy');
                    }
                }
                const types = [FieldType.ENTITY, FieldType.CURRENCY, FieldType.TEXT, FieldType.DATE, FieldType.NUMBER, FieldType.User];
                if (types.indexOf(colDef.columnType) !== -1) {
                    const fieldDefinition = colDef.columnConfig.fieldDefinition;
                    return this._services.workflowService.cellRenderer(fieldDefinition, { value: this._node.key }, true);
                }
                return this._node.key;
            }
        }
        return BLANK_VALUE;
    }

    private _initAggFun(params) {
        if (params.node.aggData) {
            const aggDataKeys = Object.keys(params.node.aggData);
            for (const key of aggDataKeys) {
                const columnState = params.columnApi.getColumn(key);
                const colDef = columnState.colDef;
                colDef.aggFunc = columnState.aggFunc;
                if (columnState.aggFunc) {
                    const types = [FieldType.ENTITY, FieldType.CURRENCY, FieldType.TEXT, FieldType.DATE, FieldType.NUMBER, FieldType.User];
                    let value = this._node.aggData[key];
                    if (!!value && !Number.isNaN(value) && colDef.fieldType && colDef.fieldType === FieldType.NUMBER && colDef.numberFormat) {
                        value = this._services.workflowService.formatNumericalValueByConfig(value, colDef.numberFormat);
                    } else if (types.indexOf(colDef.columnType) >= 0) {
                        const fieldDefinition = colDef.columnConfig.fieldDefinition;
                        value = this._services.workflowService.cellRenderer(fieldDefinition, { value });
                    }
                    const style = this._calcuteStyle(params, key);
                    this.aggFuncColumns.push({
                        key: key,
                        name: columnState.colDef.headerName,
                        aggFunc: columnState.aggFunc,
                        left: style.left,
                        width: style.width,
                        value: value
                    });
                }
            }
        }
    }

    private _calcuteStyle(params, key) {
        let left = 0;
        let width = 0;
        const columns = this._initGridHeader(params.columnApi);
        // Gets the left offset for the column's position's initialization via viewport. This is used for resolving the initialization of column position occurs after horizontal viewport is changed.
        const viewPortLeft = params.columnApi.columnController ? params.columnApi.columnController.viewportLeft : 0;
        for (const column of columns) {
            if (column.id === key) {
                width = column.width;
                left -= viewPortLeft;
                return { left, width };
            } else {
                left += column.width;
            }
        }
        return { left, width };
    }

    private _initGridHeader(gridColumnApi) {
        const infoColumns = gridColumnApi.getColumns();
        const allColumns = gridColumnApi.getColumnState();
        const tempColumns = [];
        const pinnedLeftColumns = [];
        const pinnedRightColumns = [];
        const noPinnedColumns = [];
        let columns = [];
        allColumns.forEach((column, i) => {
            if (!column.hide) {
                if (column.pinned === 'left') {
                    pinnedLeftColumns.push(column);
                } else if (column.pinned === 'right') {
                    pinnedRightColumns.push(column);
                } else {
                    noPinnedColumns.push(column);
                }
            }
        });
        columns = [].concat(pinnedLeftColumns, noPinnedColumns, pinnedRightColumns);
        columns.forEach((column) => {
            const index = infoColumns.findIndex(item => item.colId === column.colId);
            if (index !== -1) {
                tempColumns.push({
                    name: infoColumns[index].colDef.headerName,
                    width: column.width,
                    id: column.colId
                });
            }
        });
        return tempColumns;
    }

    private _resetStyle(params) {
        if (this.aggFuncColumns.length === 0) {
            return;
        }
        // if one column width change, will reset all columns width and left
        const columns = params.columnApi.getColumnState();
        for (const column of columns) {
            const tempColumn = this.aggFuncColumns.find(item => item.key === column.colId);
            if (tempColumn) {
                // use calcuteStyle, not use column left, because if column has pin, left value is error
                const style = this._calcuteStyle(params, column.colId);
                tempColumn.left = style.left;
                tempColumn.width = style.width;
            }
        }
    }

    private _updateGroupAggFuncValuePosition(moveValue) {
        this.aggFuncColumns.forEach(item => {
            item.left = item.left - moveValue;
        });
    }

    private _updateGroupAggFuncValue(params) {
        if (!params.node.aggData) {
            return;
        }
        const aggDataKeys = Object.keys(params.node.aggData);
        for (const key of aggDataKeys) {
            const columnState = params.columnApi.getColumn(key);
            const colDef = columnState.colDef;
            if (columnState.aggFunc) {
                const types = [FieldType.ENTITY, FieldType.CURRENCY, FieldType.TEXT, FieldType.DATE, FieldType.NUMBER, FieldType.User];
                let value = this._node.aggData[key];
                if (types.indexOf(colDef.columnType) !== -1) {
                    const fieldDefinition = colDef.columnConfig.fieldDefinition;
                    value = this._services.workflowService.cellRenderer(fieldDefinition, { value });
                }
                const tempColumn = this.aggFuncColumns.find(item => item.key === colDef.colId);
                tempColumn.value = value;
            }
        }
    }
}
